import { createWebPush, deleteWebPush } from 'api/WebPushAPI';
import { addNegativeToastWithErrorId } from 'modules/toastContainer';
import { FirebaseIntegrationForm } from 'modules/webPush/WebPushV2/components/Modals/components';
import { CONFIGURE_INTEGRATION_HEADER } from 'modules/webPush/WebPushV2/constants';
import {
  useModalsContext,
  useWebPushFormContext,
  useWebPushMetadataContext,
} from 'modules/webPush/WebPushV2/context';
import { ConfigureIntegrationModalProps, ModalType } from 'modules/webPush/WebPushV2/types';
import { fetchWebPushRequest } from 'modules/webPush/actions';
import { WebPushAuthType, WebPushPlatform } from 'modules/webPush/types';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { BUTTON_SIZES, PrimaryButton } from 'ui/button';
import { LongArrowRight24 } from 'ui/icons';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { formatIntegrationFieldsForSave } from '../../utils';

export const ConfigureIntegrationModal = ({
  onClose,
  authType = WebPushAuthType.FIREBASE_JSON,
}: ConfigureIntegrationModalProps) => {
  const dispatch = useDispatch();
  const { openModal } = useModalsContext();
  const { integration } = useWebPushMetadataContext();
  const { getValues, handleSubmit, setValue, watch } = useWebPushFormContext();

  const firebaseJsonFile = watch('firebaseJsonFile');

  const handleSave = handleSubmit(async () => {
    /** @note Because we do not have an actual update endpoint, we delete the web push integration and then immediately create the new one. */
    if (integration) await deleteWebPush({ platform: WebPushPlatform.FCM });

    const params = await formatIntegrationFieldsForSave(getValues());

    try {
      await createWebPush(params);
      dispatch(fetchWebPushRequest());

      onClose();
      openModal(ModalType.TestWebPushModal, { fileName: firebaseJsonFile?.name, integration });
    } catch (error) {
      dispatch(
        addNegativeToastWithErrorId(
          'Unable to save Firebase integration. Please try again.',
          'createWebPushIntegration',
          error,
        ),
      );
    }
  });

  useEffect(() => {
    setValue('authType', authType);
  }, [authType]);

  return (
    <Modal id="web-push-integration-create-update-modal" isModalOpen onClose={onClose}>
      <ModalHeader>{CONFIGURE_INTEGRATION_HEADER}</ModalHeader>
      <ModalBody>
        <FirebaseIntegrationForm />
      </ModalBody>
      <ModalFooter>
        <PrimaryButton size={BUTTON_SIZES.LARGE} onClick={handleSave}>
          Save
          <LongArrowRight24 />
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  );
};
